/** Small presentational buttons used by the Home tab's toolbars, tab bar and menus. */

import type React from "react";

/**
 * Undo / Redo / Restore control.
 *
 * Always rendered, so the toolbar keeps a stable shape from the very first
 * version — a control that appears only once it becomes usable reads as a
 * missing feature, and moves every button beside it when it finally shows up.
 * `enabled` greys it out instead.
 */
export function HistoryIconButton({
  icon,
  label,
  title,
  enabled,
  onClick,
  tone = 'neutral',
}: {
  icon: string;
  label: string;
  title: string;
  enabled: boolean;
  onClick: () => void;
  tone?: 'neutral' | 'positive';
}) {
  const positive = tone === 'positive';
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={!enabled}
      aria-disabled={!enabled}
      style={{
        background: positive && enabled ? '#f0fdf4' : 'none',
        border: `1px solid ${positive && enabled ? '#86efac' : '#e2e8f0'}`,
        borderRadius: 6,
        padding: '3px 6px',
        cursor: enabled ? 'pointer' : 'default',
        color: positive && enabled ? '#166534' : '#64748b',
        fontWeight: positive ? 600 : 400,
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
        opacity: enabled ? 1 : 0.4,
      }}
    >
      <span style={{ fontSize: 13, lineHeight: 1 }}>{icon}</span>
      <span style={{ fontSize: 9, lineHeight: 1 }}>{label}</span>
    </button>
  );
}

/** Dim veil + label over the pane a release would hide. */
export function HidePaneHint({ name }: { name: string }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute', inset: 0, zIndex: 20, pointerEvents: 'none',
        background: 'rgba(15, 23, 42, 0.55)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, textAlign: 'center', padding: 8,
      }}
    >
      Release to hide {name}
    </div>
  );
}

export function SplitControlButton({
  isSplit,
  splitDirection,
  onEnableSplit,
  onToggleDirection,
}: {
  isSplit: boolean;
  splitDirection: 'horizontal' | 'vertical';
  onEnableSplit: () => void;
  onToggleDirection: () => void;
}) {
  if (isSplit) {
    return (
      <button
        onClick={onToggleDirection}
        title={splitDirection === 'horizontal' ? 'Switch to vertical (stacked) split' : 'Switch to horizontal (side-by-side) split'}
        style={{
          background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 20, padding: '4px 12px',
          fontSize: 12, cursor: 'pointer', color: '#334155', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
          marginLeft: 'auto', alignSelf: 'center',
        }}
      >
        {splitDirection === 'horizontal' ? '⇅ Stack' : '⇆ Side-by-side'}
      </button>
    );
  }
  return (
    <button
      onClick={onEnableSplit}
      title="Enable split screen — see Counter and Officers side by side"
      style={{
        background: '#667eea', color: 'white', border: 'none', borderRadius: 20, padding: '5px 14px',
        fontSize: 13, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5,
        boxShadow: '0 1px 4px rgba(102,126,234,0.35)', letterSpacing: '0.01em', flexShrink: 0,
        marginLeft: 'auto', alignSelf: 'center',
      }}
    >
      ◫ Split view
    </button>
  );
}

/** A single row inside the mobile "More" popover — icon, label and an optional
 * unread badge laid out horizontally, unlike the icon-over-label nav buttons
 * this same action renders as elsewhere. */
function MenuRowButton({
  icon,
  label,
  badgeCount,
  highlighted,
  trailing,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  badgeCount?: number;
  highlighted?: boolean;
  trailing?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 14, fontWeight: 600, color: highlighted ? '#856404' : '#334155', textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 17, flexShrink: 0, display: 'flex', alignItems: 'center' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {trailing}
      {!!badgeCount && (
        <span style={{
          background: '#dc3545', color: 'white', borderRadius: '50%', minWidth: 18, height: 18,
          padding: '0 4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, flexShrink: 0,
        }}>
          {badgeCount}
        </span>
      )}
    </button>
  );
}

export function InboxTabButton({
  variant,
  unreadCautionCount,
  onClick,
}: {
  variant: 'desktop' | 'mobile-menu';
  unreadCautionCount: number;
  onClick: () => void;
}) {
  if (variant === 'mobile-menu') {
    return <MenuRowButton icon="📬" label="Inbox" badgeCount={unreadCautionCount} highlighted={unreadCautionCount > 0} onClick={onClick} />;
  }
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
        alignSelf: 'center', flexShrink: 0,
        background: unreadCautionCount > 0 ? '#fff3cd' : '#e0e7ff',
        border: `1px solid ${unreadCautionCount > 0 ? '#ffc107' : '#818cf8'}`,
        borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
        color: unreadCautionCount > 0 ? '#856404' : '#3730a3',
      }}
    >
      📬 Inbox
      {unreadCautionCount > 0 && (
        <span
          style={{
            background: '#dc3545', color: 'white', borderRadius: '50%',
            width: '18px', height: '18px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '11px', fontWeight: 700,
          }}
        >
          {unreadCautionCount}
        </span>
      )}
    </button>
  );
}

/** No Relief tab-bar button — Inbox's shape, with a badge counting the calls
 * still owed (officers not yet told there's no relief, or that relief is now coming). */
export function NoReliefTabButton({
  variant,
  pendingCount,
  onClick,
}: {
  variant: 'desktop' | 'mobile-menu';
  pendingCount: number;
  onClick: () => void;
}) {
  if (variant === 'mobile-menu') {
    return <MenuRowButton icon="🚫" label="No Relief" badgeCount={pendingCount} onClick={onClick} />;
  }
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
        alignSelf: 'center', flexShrink: 0,
        background: '#fee2e2', border: '1px solid #f87171',
        borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
        color: '#991b1b',
      }}
    >
      🚫 No Relief
      {pendingCount > 0 && (
        <span
          style={{
            background: '#dc3545', color: 'white', borderRadius: '9px',
            minWidth: '18px', height: '18px', padding: '0 4px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '11px', fontWeight: 700, boxSizing: 'border-box',
          }}
        >
          {pendingCount}
        </span>
      )}
    </button>
  );
}

/** Notes/Contacts tab-bar buttons — same portal pattern and visual weight as Inbox,
 * without its unread-badge behaviour. */
/** Notes gets a sticky-note amber, Contacts the WhatsApp green it leads to —
 * a plain grey pill read as equally low-priority as every other chrome
 * element, so neither stood out enough to be found at a glance. */
const ICON_TAB_ACCENTS = {
  notes: { bg: '#fef9c3', border: '#fbbf24', color: '#854d0e' },
  contacts: { bg: '#dcfce7', border: '#25D366', color: '#0f7a3d' },
} as const;

/** Understated count: tinted outline chip, no red fill, so it never competes
 * with the Inbox / No Relief alert badges. */
function CountChip({ count, accent }: { count: number; accent: keyof typeof ICON_TAB_ACCENTS }) {
  const { border, color } = ICON_TAB_ACCENTS[accent];
  return (
    <span
      title={`${count} note${count === 1 ? '' : 's'} with content`}
      style={{
        color, border: `1px solid ${border}`, borderRadius: 9, minWidth: 16, height: 16,
        padding: '0 4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 600, lineHeight: 1, boxSizing: 'border-box', opacity: 0.85,
      }}
    >
      {count}
    </span>
  );
}

export function IconTabButton({
  variant,
  icon,
  label,
  accent,
  count,
  onClick,
}: {
  variant: 'desktop' | 'mobile-menu';
  icon: React.ReactNode;
  label: string;
  accent: keyof typeof ICON_TAB_ACCENTS;
  /** Persistent tally (not an unread alert): rendered as a muted chip in the
   * button's own accent colour, hidden at 0. */
  count?: number;
  onClick: () => void;
}) {
  if (variant === 'mobile-menu') {
    return (
      <MenuRowButton
        icon={icon}
        label={label}
        onClick={onClick}
        trailing={count ? <CountChip count={count} accent={accent} /> : undefined}
      />
    );
  }
  const { bg, border, color } = ICON_TAB_ACCENTS[accent];
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
        alignSelf: 'center', flexShrink: 0,
        background: bg, border: `1px solid ${border}`,
        borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
        color,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span> {label}
      {!!count && <CountChip count={count} accent={accent} />}
    </button>
  );
}
